import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "./CourseContent.css";
import { useGlobal } from "../context/GlobalContext";
import Header from "../Components/Header";
import CourseLeftNav from "./CourseLeftNav";
import QuizQuestionPage from "../pages/QuizQuestionPage";
import QuizSummary from "../ResumeTrainingPage/QuizSummary";
import Player from "../Components/Player";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa6";
import QuizDialogue from "../Components/QuizDialogue";
import { SvgIcon } from "../Components/SvgIcon";
import NextLessonModal from "../Components/NextLessonModal";
import YouTubePlayer from "../Components/YouTubePlayer";
import { motion, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const slideUp = {
  hidden: { opacity: 0, y: 36, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.7, ease: EASE } },
};

// card that staggers its children in
const cardVariants = {
  hidden: { opacity: 0, y: 36, scale: 0.98 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.7,
      ease: EASE,
      staggerChildren: 0.09,
      delayChildren: 0.15,
    },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

export default function CourseContent({ setIsSidebarOpen, isSidebarOpen }) {
  const reduceMotion = useReducedMotion();
  const {
    courseData,
    userProgress,
    handleItemCompletion,
    currentItem,
    setCurrentItem,
    findNextItem,
  } = useGlobal();

  const { id } = useParams();
  let { state } = useLocation();

  const sectionName = state?.sectionName;
  const moduleName = state?.moduleName;
  const navigate = useNavigate();

  const [isVideoCompleted, setIsVideoCompleted] = useState(
    userProgress?.currentItem?.status
  );
  const [currentSectionId, setCurrentSectionId] = useState(null);
  const [currentModuleId, setCurrentModuleId] = useState(null);
  const [currentSectionTitle, setCurrentSectionTitle] = useState(null);
  const [isSelected, setIsSelected] = useState(null);
  const [videoSources, setVideoSources] = useState([]);

  const [isQuizDialogueOpen, setIsQuizDialogueOpen] = useState(false);
  const [nextQuizItem, setNextQuizItem] = useState(null);
  const [isQuizStarted, setIsQuizStarted] = useState(false);

  const [showNextLessonModal, setShowNextLessonModal] = useState(false);
  const [nextLesson, setNextLesson] = useState(null);

  const [isDesktopLayout, setIsDesktopLayout] = useState(
    () => window.innerWidth >= 1280
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1280px)");
    const handleChange = (e) => setIsDesktopLayout(e.matches);

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    const section = courseData?.find((s) => s.title === sectionName);
    const module = section?.modules.find((m) => m.title === moduleName);
    const content = module?.content.find((c) => c.id === id);

    if (
      content?.type === "quiz" &&
      isQuizStarted &&
      currentItem?.type === "quiz" &&
      currentItem?.id !== content.id
    ) {
      setIsQuizDialogueOpen(true);
      setNextQuizItem(content);
      handleStartQuiz();
    } else {
      setCurrentSectionId(section?.id || userProgress?.currentSection?.id);
      setCurrentModuleId(module?.id || userProgress?.currentModule?.id);
      setCurrentSectionTitle(
        section?.title || userProgress?.currentSection?.title
      );
      if (content) {
        setCurrentItem(content);
        setIsSelected(content);
        if (content.type !== "quiz") {
          setIsQuizStarted(false);
          handleStartQuiz();
        }
      } else {
        setCurrentItem(userProgress?.currentItem);
        handleStartQuiz();
      }
    }
  }, [
    courseData,
    sectionName,
    moduleName,
    id,
    setCurrentItem,
    userProgress,
    isQuizStarted,
    currentItem,
  ]);

  const handleVideoComplete = useCallback(
    async (currentItem, sectionId, currentModuleId) => {
      console.log("Video ended, handling completion");

      setIsVideoCompleted(true);
      await handleItemCompletion(currentItem, sectionId, currentModuleId);

      const nextItem = findNextItem(currentItem, sectionId);
      if (nextItem && nextItem.type === "video") {
        setNextLesson(nextItem);
        setShowNextLessonModal(true);
      } else {
        if (nextItem) {
          navigate(`/resume/course-content/items/${nextItem.id}`, {
            state: {
              sectionName,
              moduleName: nextItem.module.title,
              type: nextItem.type,
            },
          });
        }
      }
    },
    [handleItemCompletion, findNextItem, navigate, sectionName]
  );

  const handleNextLessonModalClose = (shouldNavigate = true) => {
    setShowNextLessonModal(false);

    if (shouldNavigate && nextLesson) {
      navigate(`/resume/course-content/items/${nextLesson.id}`, {
        state: {
          sectionName,
          moduleName: nextLesson.module.title,
          type: nextLesson.type,
        },
      });
    }
  };

  const handleNextItem = (currentItem, sectionId) => {
    const nextItem = findNextItem(currentItem, sectionId);
    if (nextItem) {
      navigate(`/resume/course-content/items/${nextItem.id}`, {
        state: {
          sectionName,
          moduleName: nextItem.module.title,
          type: nextItem.type,
        },
      });
    }
  };

  const formatURL = (url, quality) => {
    if (!url) return url;
    if (quality === 1080) return url;

    return url.replace("1080p.", `${quality}p.`);
  };

  const getVideoSources = useCallback(() => {
    const item = currentItem;

    if (item?.sizes) {
      const sizes = item.sizes
        .sort((a, b) => b - a)
        .map((size) => ({
          src: formatURL(item.url, size),
          type: "video/mp4",
          size: size,
        }));
      setVideoSources(sizes);
    }
  }, [currentItem]);

  useEffect(() => {
    getVideoSources();
    handleStartQuiz();
  }, [currentItem, getVideoSources]);

  const plyrOptions = {
    controls: [
      "play-large",
      "play",
      "progress",
      "current-time",
      "mute",
      "volume",
      "settings",
      "pip",
      "airplay",
      "fullscreen",
    ],
    settings: ["quality", "speed", "loop"],
  };

  const handleQuizDialogueConfirm = () => {
    setIsQuizStarted(false);
    setCurrentItem(nextQuizItem);
    setIsSelected(nextQuizItem);
    setIsQuizDialogueOpen(false);
  };

  const handleQuizDialogueClose = () => {
    setIsQuizDialogueOpen(false);
  };

  const handleStartQuiz = () => {
    setIsQuizStarted(true);
  };

  // Handle YouTube video end
  const handleYouTubeVideoEnd = useCallback(() => {
    handleVideoComplete(currentItem, currentSectionId, currentModuleId);
  }, [currentItem, currentSectionId, currentModuleId, handleVideoComplete]);

  const renderVideoPlayer = useCallback(() => {
    // Check if it's a YouTube video based on uploadType
    const isYouTubeVideo = currentItem?.uploadType === "url";
    return (
      <div className="relative h-0 pt-[56.25%] rounded-[12px] overflow-hidden border-[1px] border-borderColor shadow-[0_20px_50px_-20px_rgba(66,104,255,0.45)] ring-1 ring-white/5 transition-shadow duration-500 hover:shadow-[0_24px_60px_-18px_rgba(66,104,255,0.6)]">
        {isYouTubeVideo ? (
          <YouTubePlayer
            videoUrl={currentItem.url}
            onVideoEnd={handleYouTubeVideoEnd}
            currentItem={currentItem}
          />
        ) : (
          <Player
            plyrOptions={plyrOptions}
            videoSource={videoSources}
            currentItem={currentItem}
            moduleId={currentModuleId}
            sectionId={currentSectionId}
            handleVideoEnd={handleVideoComplete}
          />
        )}
      </div>
    );
  }, [
    currentItem,
    videoSources,
    handleVideoComplete,
    handleYouTubeVideoEnd,
    currentSectionId,
    currentModuleId,
  ]);

  const renderContent = () => {
    if (currentItem?.type === "quiz") {
      return isQuizStarted ? (
        <QuizQuestionPage
          quiz={currentItem}
          sectionId={currentSectionId}
          sectionName={currentSectionTitle}
          moduleId={currentModuleId}
        />
      ) : (
        <QuizSummary
          quiz={currentItem}
          handleStartQuiz={handleStartQuiz}
          isLoading={false}
        />
      );
    }

    const title = currentItem?.title || userProgress?.currentItem?.title;
    const description =
      currentItem?.description || userProgress?.currentItem?.description;

    return (
      <motion.div
        initial={reduceMotion ? "show" : "hidden"}
        animate="show"
        variants={cardVariants}
        className="resume-video-scroll relative flex flex-col gap-5 xl:h-[calc(100vh-123px)] xl:w-[70%] border-[1px] border-borderColor bg-bgColor rounded-[15px] p-[15px] xl:p-6 overflow-hidden overflow-y-auto shadow-[0_20px_60px_-30px_rgba(0,0,0,0.8)]"
      >
        {/* ambient glow */}
        <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#4268FF] opacity-10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-24 h-64 w-64 rounded-full bg-[#113BE1] opacity-10 blur-3xl" />

        {/* title block */}
        <motion.div variants={fadeUp} className="relative flex flex-col gap-3">
          {(sectionName || moduleName) && (
            <div className="hidden xl:flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-[#A0AEC0]">
              {sectionName && <span>{sectionName}</span>}
              {sectionName && moduleName && (
                <span className="h-1 w-1 rounded-full bg-[#4268FF]" />
              )}
              {moduleName && <span>{moduleName}</span>}
            </div>
          )}

          <h2 className="hidden xl:block text-left font-bold text-[22px] leading-tight text-white">
            {title}
          </h2>

          {/* mobile / tablet header */}
          <div className="flex items-center justify-between gap-3 xl:hidden">
            <button
              type="button"
              aria-label="Précédent"
              onClick={() => navigate(-1)}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#ffffff1a] border-[1px] border-borderColor transition-all duration-300 hover:bg-[#4268FF]/30 active:scale-90"
            >
              <FaAngleLeft className="text-white w-3.5 h-3.5" />
            </button>
            <h2 className="flex-1 text-center font-semibold text-[14px] sm:text-[16px] leading-snug text-white">
              {title}
            </h2>
            <button
              type="button"
              aria-label="Suivant"
              onClick={() => handleNextItem(currentItem, currentSectionId)}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#ffffff1a] border-[1px] border-borderColor transition-all duration-300 hover:bg-[#4268FF]/30 active:scale-90"
            >
              <FaAngleRight className="text-white w-3.5 h-3.5" />
            </button>
          </div>
        </motion.div>

        {/* video */}
        <motion.div variants={fadeUp} className="relative">
          {renderVideoPlayer()}
        </motion.div>

        {/* description card */}
        <motion.div
          variants={fadeUp}
          className="relative flex flex-col gap-3 rounded-[12px] bg-[#ffffff0d] border-[1px] border-borderColor p-4 xl:p-5"
        >
          <div className="flex items-center gap-2.5">
            <span className="h-4 w-[3px] rounded-full bg-gradient-to-b from-[#4268FF] to-[#113BE1]" />
            <h2 className="text-white text-lg font-semibold leading-normal text-left lg:text-[14px] xl:text-[16px]">
              Description
            </h2>
          </div>
          <div
            className="text-left text-[12px] lg:text-[13px] xl:text-[15px] leading-relaxed text-[#A0AEC0]"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </motion.div>

        {/* mobile CTA */}
        <motion.div variants={fadeUp} className="relative w-full xl:hidden">
          <motion.button
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.97 }}
            className={`primary-btn flex w-full p-[12px] justify-center items-center rounded-[10px] text-white font-bold text-[12px] tracking-wider uppercase bg-gradient-to-r from-[#113BE1] via-[#4268FF] to-[#113BE1] shadow-[0_10px_30px_-10px_#4268FF] transition-shadow duration-300 hover:shadow-[0_14px_36px_-10px_#4268FF]`}
          >
            Commencer la vidéo
          </motion.button>
        </motion.div>

        {/* completed badge */}
        {isVideoCompleted && (
          <motion.div
            variants={fadeUp}
            className="relative flex w-fit items-center gap-2 rounded-full bg-[#ffffff1a] border-[1px] border-borderColor px-4 py-2"
          >
            <motion.span
              initial={reduceMotion ? false : { scale: 0, rotate: -45 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 15, delay: 0.3 }}
              className="flex"
            >
              <SvgIcon name="tickMark" />
            </motion.span>
            <p className="text-xs font-medium text-white">
              Tu as consulté cette vidéo
            </p>
          </motion.div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="flex flex-col h-[100%]">
      <div className="w-full">
        <Header
          PageName="Formation"
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      </div>
      <div className="nav-top">
        <h2 className="text-[20px] font-semibold py-1 sm:py-3 text-left text-white">
          Sommaire
        </h2>
      </div>
      <div className="flex flex-col gap-6 xl:flex-row">


        {isDesktopLayout ? (
          <>
            <motion.div
              initial={reduceMotion ? "show" : "hidden"}
              animate="show"
              variants={slideUp}
              className="course-left-nav"
            >
              <CourseLeftNav />
            </motion.div>
            {renderContent()}
          </>
        ) : (
          <>
            {isSelected ? (
              <motion.div
                initial={reduceMotion ? "show" : "hidden"}
                animate="show"
                variants={slideUp}
                className="flex w-full flex-col"
              >
                <motion.div
                  whileHover={{ x: -3 }}
                  whileTap={{ scale: 0.96 }}
                  className="group mb-6 flex w-fit cursor-pointer items-center gap-[10px] rounded-full bg-[#ffffff1a] border-[1px] border-borderColor px-4 py-2 transition-colors duration-300 hover:bg-[#4268FF]/20"
                  onClick={() => {
                    setIsSelected(null);
                    navigate("/resume");
                  }}
                >
                  <FaAngleLeft className="text-white w-3 h-3 transition-transform duration-300 group-hover:-translate-x-0.5" />
                  <span className="text-[14px] font-medium text-white">
                    Revenir au sommaire
                  </span>
                </motion.div>
                {renderContent()}
              </motion.div>
            ) : (
              <motion.div
                initial={reduceMotion ? "show" : "hidden"}
                animate="show"
                variants={slideUp}
                className="course-left-nav"
              >
                <CourseLeftNav />
              </motion.div>
            )}
          </>
        )}
      </div>

      <QuizDialogue
        isOpen={isQuizDialogueOpen}
        onClose={handleQuizDialogueClose}
        onConfirm={handleQuizDialogueConfirm}
      />
      <NextLessonModal
        isOpen={showNextLessonModal}
        onClose={handleNextLessonModalClose}
        nextItem={nextLesson}
      />
    </div>
  );
}